import { useState } from "react"
import { ModalShell } from "@/components/portal/ModalShell"
import { toast } from "@/components/portal/Toast"
import { PersonPunkt } from "./Person"
import { anzeigeName, naechsterFreitag, tagInTagen, useUebergeben, useZustaendige } from "./zusammenarbeit"

type Wv = "keine" | "morgen" | "freitag"

/**
 * Mail übergeben: Person wählen, Stand dazuschreiben, optional wieder vorlegen.
 *
 * Der Stand ist kein Pflichtfeld — aber das Backend hinterlässt immer eine
 * Spur am Verlauf („Übergeben an …"), damit niemand raten muss, seit wann die
 * Mail bei wem liegt.
 */
export function UebergabeDialog({ emailId, offen, onClose, aktuell }: {
  emailId: number
  offen: boolean
  onClose: () => void
  aktuell?: string | null
}) {
  const personen = useZustaendige()
  // `undefined` = noch niemand gewählt. Ohne bisherige Zuständigkeit soll
  // nicht „Niemand" vorausgewählt sein — das wäre ein Klick ins Leere.
  const [an, setAn] = useState<string | null | undefined>(aktuell ?? undefined)
  const [stand, setStand] = useState("")
  const [wv, setWv] = useState<Wv>("keine")
  const m = useUebergeben(emailId)

  const absenden = () => {
    const wiedervorlage_am = wv === "morgen" ? tagInTagen(1) : wv === "freitag" ? naechsterFreitag() : null
    if (an === undefined) return
    m.mutate({ zustaendig: an, notiz: stand.trim() || undefined, wiedervorlage_am }, {
      onSuccess: () => {
        toast(an ? `An ${anzeigeName(an)} übergeben` : "Zuständigkeit entfernt", "success")
        onClose()
      },
      onError: (e) => toast("Übergeben fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error"),
    })
  }

  return (
    <ModalShell
      open={offen}
      onClose={onClose}
      titel="Übergeben"
      untertitel="Gilt für den ganzen Verlauf. Die Kollegin sieht die Mail unter „Meine“."
      breite={480}
      fuss={
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", width: "100%" }}>
          <button type="button" className="pp-btn" onClick={onClose}>Abbrechen</button>
          <button type="button" className="pp-btn pp-btn-primary" disabled={m.isPending || an === undefined} onClick={absenden}>
            {m.isPending ? "Übergibt…" : an === undefined ? "Person wählen" : an ? `An ${anzeigeName(an)} übergeben` : "Niemandem zuweisen"}
          </button>
        </div>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "16px 20px" }}>
        <div className="ev2-personen" role="radiogroup" aria-label="An wen">
          {(personen.data || []).map((p) => (
            <button key={p} type="button" role="radio" aria-checked={an === p}
              className={an === p ? "ev2-personwahl aktiv" : "ev2-personwahl"}
              onClick={() => setAn(p)}>
              <PersonPunkt name={p} groesse={34} titel="" />
              <span>{anzeigeName(p)}</span>
            </button>
          ))}
          <button type="button" role="radio" aria-checked={an === null}
            className={an === null ? "ev2-personwahl aktiv" : "ev2-personwahl"}
            onClick={() => setAn(null)}>
            <PersonPunkt name={null} groesse={34} titel="" />
            <span>Niemand</span>
          </button>
        </div>
        {personen.isError && <div className="ev2-hinweiszeile">Personen konnten nicht geladen werden.</div>}
        <label htmlFor="ev2-stand" style={{ fontSize: 13, color: "var(--text-secondary)" }}>
          Was muss {an ? anzeigeName(an) : "die Kollegin"} wissen?
        </label>
        <textarea id="ev2-stand" rows={3} value={stand} onChange={(e) => setStand(e.target.value)}
          placeholder="z. B. Termin steht, Antwort fehlt noch." className="ev2-textfeld" />
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "var(--text-secondary)", marginRight: 4 }}>Wieder vorlegen</span>
          {([["keine", "Nicht nötig"], ["morgen", "Morgen"], ["freitag", "Freitag"]] as [Wv, string][]).map(([k, l]) => (
            <button key={k} type="button" aria-pressed={wv === k}
              className={wv === k ? "ev2-pille aktiv" : "ev2-pille"} onClick={() => setWv(k)}>{l}</button>
          ))}
        </div>
      </div>
    </ModalShell>
  )
}
